import React, { useRef, useEffect, useCallback, useState } from 'react'
import { useStore, useEffectsState, usePerformanceConfig } from '../../state/store'
import { renderer } from '../../core/Renderer'
import { AnimationController } from '../../core/animation'
import type { AnimationContext } from '../../core/animation/types'
import { EffectsProcessor } from '../../core/effects'
import {
  scaleImageToCharLimit,
  type ScaleResult,
  type ScaleConfig,
} from '../../core/performance/imageScale'
import StyleSelector from './StyleSelector'
import AnimationPanel from './AnimationPanel'
import EffectsPanel from './EffectsPanel'
import PresetPanel from './PresetPanel'
import SettingsPanel from './SettingsPanel'
import EngineParamsPanel from './EngineParamsPanel'
import ExportPanel from './ExportPanel'
import PerformanceInfoPanel from './PerformanceInfoPanel'

const App: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const animationControllerRef = useRef<AnimationController | null>(null)
  const effectsProcessorRef = useRef<EffectsProcessor | null>(null)

  const effectsState = useEffectsState()
  const performanceConfig = usePerformanceConfig()

  const [scaleInfo, setScaleInfo] = useState<ScaleResult | null>(null)
  const [showPerfPanel, setShowPerfPanel] = useState(false)

  const settingsRef = useRef(useStore.getState().settings)
  const sourceImageRef = useRef<HTMLImageElement | null>(null)
  const effectsStateRef = useRef(effectsState)

  const {
    settings,
    sourceImage,
    imageDataUrl,
    isLoading,
    error,
    setSourceImage,
    clearSourceImage,
    setLoading,
    setError,
  } = useStore()

  useEffect(() => {
    settingsRef.current = settings
  }, [settings])

  useEffect(() => {
    sourceImageRef.current = sourceImage
  }, [sourceImage])

  useEffect(() => {
    effectsStateRef.current = effectsState
  }, [effectsState])

  const supportsAnimation = true

  const animationEnabled = (() => {
    if (!supportsAnimation) return false
    return settings.ditherAnimationEnabled || settings.dither2AnimationEnabled
  })()

  const renderAnimationFrame = useCallback((context: AnimationContext) => {
    const currentSource = sourceImageRef.current
    const currentCanvas = canvasRef.current
    const currentSettings = settingsRef.current
    const currentEffectsState = effectsStateRef.current
    const effectsProcessor = effectsProcessorRef.current

    if (!currentSource || !currentCanvas) return

    try {
      renderer.updateSettings(currentSettings)
      renderer.renderAnimated(currentSource, currentCanvas, context)

      if (effectsProcessor && currentEffectsState) {
        effectsProcessor.update(context.deltaTime)
        effectsProcessor.syncEffectsFromState(currentEffectsState)
        effectsProcessor.apply(currentCanvas, currentEffectsState)
      }
    } catch (err) {
      console.error('Animation render error:', err)
    }
  }, [])

  const renderStatic = useCallback(() => {
    if (!sourceImage || !canvasRef.current) return

    try {
      setLoading(true)
      renderer.render(sourceImage, canvasRef.current, settings)

      if (effectsProcessorRef.current && effectsState) {
        effectsProcessorRef.current.syncEffectsFromState(effectsState)
        effectsProcessorRef.current.apply(canvasRef.current, effectsState)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '渲染失败')
    } finally {
      setLoading(false)
    }
  }, [sourceImage, settings, effectsState, setLoading, setError])

  useEffect(() => {
    animationControllerRef.current = new AnimationController({
      enabled: false,
      targetFPS: 60,
      onFrame: renderAnimationFrame,
    })

    effectsProcessorRef.current = new EffectsProcessor()

    return () => {
      if (animationControllerRef.current) {
        animationControllerRef.current.destroy()
        animationControllerRef.current = null
      }
      effectsProcessorRef.current = null
    }
  }, [renderAnimationFrame])

  useEffect(() => {
    if (!sourceImage || !canvasRef.current || animationEnabled) return
    if (!effectsProcessorRef.current) return
    renderStatic()
  }, [effectsState, sourceImage, animationEnabled, renderStatic])

  useEffect(() => {
    const controller = animationControllerRef.current
    if (canvasRef.current && controller && sourceImage) {
      const timer = setTimeout(() => {
        if (canvasRef.current && controller) {
          controller.attachToCanvas(canvasRef.current)
        }
      }, 100)

      return () => {
        clearTimeout(timer)
        if (controller) {
          controller.detachFromCanvas()
        }
      }
    }
  }, [sourceImage])

  useEffect(() => {
    const controller = animationControllerRef.current
    if (!controller) return

    if (animationEnabled && sourceImage) {
      controller.setEnabled(true)
      controller.start()
    } else {
      controller.stop()
      if (sourceImage) {
        renderStatic()
      }
    }
  }, [animationEnabled, sourceImage, renderStatic])

  useEffect(() => {
    const controller = animationControllerRef.current
    if (!controller) return
    controller.updateWaveSettings(settings)
  }, [settings])

  useEffect(() => {
    if (sourceImage && !animationEnabled) {
      renderStatic()
    }
  }, [sourceImage, settings, animationEnabled, renderStatic])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'p' || e.key === 'P') {
        if (document.activeElement?.tagName !== 'INPUT' &&
            document.activeElement?.tagName !== 'TEXTAREA') {
          setShowPerfPanel((prev) => !prev)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const loadImageFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('请上传图片文件')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      const img = new Image()
      img.onload = () => {
        const scaleConfig: ScaleConfig = {
          maxChars: performanceConfig.maxCharsLimit,
          fontSize: settings.fontSize,
          charSpacing: settings.charSpacing,
          preserveOriginal: performanceConfig.preserveOriginalSize,
        }

        const scaleResult = scaleImageToCharLimit(img, scaleConfig)
        setScaleInfo(scaleResult)

        const scaledImg = new Image()
        scaledImg.onload = () => {
          setSourceImage(scaledImg, dataUrl)
        }
        scaledImg.src = scaleResult.canvas.toDataURL('image/png')
      }
      img.onerror = () => {
        setError('图片加载失败')
      }
      img.src = dataUrl
    }
    reader.onerror = () => {
      setError('文件读取失败')
    }
    reader.readAsDataURL(file)
  }, [performanceConfig, settings.fontSize, settings.charSpacing, setError, setSourceImage])

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    loadImageFile(file)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    const file = event.dataTransfer.files[0]
    if (!file) return
    loadImageFile(file)
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
  }

  const statusText = animationEnabled && sourceImage ? '动画运行中' : sourceImage ? '静态预览' : '等待导入图片'

  return (
    <div className="xp-shell">
      <div className="xp-window overflow-hidden">
        <header className="xp-title-bar">
          <div className="flex items-center gap-2 min-w-0">
            <span className="xp-title-text truncate">ASCII Art Generator</span>
          </div>
          <div className="xp-title-controls" aria-hidden="true">
            <span className="xp-title-button">_</span>
            <span className="xp-title-button">□</span>
            <span className="xp-title-button">×</span>
          </div>
        </header>

        <div className="xp-window-body">
          <div className="flex flex-col lg:flex-row min-h-[calc(100vh-122px)]">
            <aside className="xp-sidebar w-full lg:w-[340px] p-3 overflow-y-auto lg:max-h-[calc(100vh-122px)]">
              <div className="space-y-3">
                <div className="panel-section">
                  <div className="xp-section-header">
                    <div>
                      <h1 className="panel-title">工作站</h1>
                      <p className="xp-muted mt-1 text-[15px] font-semibold">ASCII 图像转换与导出工具</p>
                    </div>
                    <span className="xp-badge">{statusText}</span>
                  </div>
                </div>

                <div className="panel-section">
                  <div className="xp-section-header">
                    <h3 className="panel-title">图片导入</h3>
                  </div>
                  <div className="xp-section-body space-y-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="btn-primary w-full"
                    >
                      浏览图片...
                    </button>
                    {imageDataUrl && (
                      <button
                        onClick={clearSourceImage}
                        className="btn-secondary w-full"
                      >
                        清除当前图片
                      </button>
                    )}
                    <div className="xp-note">
                      支持拖拽或手动选择 JPG、PNG、WEBP 图片。
                    </div>
                  </div>
                </div>

                <PresetPanel />
                <StyleSelector />
                <EngineParamsPanel />
                <AnimationPanel />
                <EffectsPanel />

                <SettingsPanel />

                <ExportPanel
                  canvas={canvasRef.current}
                  sourceImage={sourceImage}
                  settings={settings}
                  effectsState={effectsState}
                />
              </div>
            </aside>

            <main
              className="xp-workspace flex-1 p-4 flex items-center justify-center overflow-auto lg:sticky lg:top-0 lg:h-[calc(100vh-122px)] lg:self-start"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="w-full max-w-[1100px] flex flex-col items-center">
                {error && (
                  <div className="xp-error mb-3 w-full">
                    {error}
                  </div>
                )}

                {!sourceImage ? (
                  <div className="xp-preview-shell">
                    <div className="xp-preview-frame min-h-[520px] flex items-center justify-center">
                      <div className="xp-empty-state">
                        <div className="panel-title">预览窗口</div>
                        <p>将图片拖放到此区域，或使用左侧“浏览图片...”按钮导入。</p>
                        <p className="text-[15px] font-semibold">导入后可继续调整参数、动画、特效和导出设置。</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="xp-preview-shell inline-block">
                    <div className="xp-preview-frame relative inline-block max-w-full">
                      <canvas
                        ref={canvasRef}
                        className="xp-canvas max-w-full max-h-[70vh]"
                        style={{
                          backgroundColor: settings.backgroundColor,
                        }}
                      />

                      {showPerfPanel && (
                        <div className="absolute top-2 right-2 z-20">
                          <PerformanceInfoPanel
                            getCurrentFPS={() => animationControllerRef.current?.getCurrentFPS() || 0}
                            getAverageFPS={() => animationControllerRef.current?.getAverageFPS() || 0}
                            getDegradeLevel={() => animationControllerRef.current?.getDegradeLevel() || 'none'}
                            scaleInfo={scaleInfo}
                            isAnimating={animationEnabled && !!sourceImage}
                            isLoading={isLoading}
                            variant="compact"
                          />
                        </div>
                      )}

                      {isLoading && (
                        <div className="xp-overlay z-10">
                          <div className="xp-note">正在渲染，请稍候...</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </main>
          </div>

          <footer className="xp-status-bar">
            <div className="flex flex-wrap items-center gap-2">
              <span className="xp-status-field">ASCII Art Generator</span>
              <span className="xp-status-field">状态: {statusText}</span>
              <span className="xp-status-field">性能面板快捷键: P</span>
            </div>
          </footer>
        </div>
      </div>
    </div>
  )
}

export default App
